import React, { Fragment } from 'react';
import { DataGridBaseProps } from './models/DataGridBaseProps';
import { useStateDataGrid } from './hooks/useStateDataGrid';
import DataQuery from 'data/DataQuery';
import { useQuery } from 'data/hooks/useQuery';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import NoContentWrapper from '../noContentWrapper/NoContentWrapper';
import DataGridHeader from './DataGridHeader';
import Pagination from '../pagination/Pagination';
import DataFilterModal from '../dataFilterModal/DataFilterModal';
import DataGridRow from './DataGridRow';
import SortingMenu from '../sorting/SortingMenu';

interface Props<T> extends DataGridBaseProps<T>  {
  pageSize?: number,
  filterJson?: string;
}

const DataGridState = <T extends object>({
  url,
  renderItem,
  renderHeader,
  pageSize,
  filterJson,
  sortingJson,
  renderGridHeaderBtns,
  itemAsCard = true,
  renderNoContent,
  routeParams,
  noFilterQueryParams,
  externalDependencies,
  renderCustomGridHeader
}: Props<T>) => {
  //#region data grid hook
  const {
    dataGrid,
    descriptors,
    setDescriptors,
    showFilter,
    setShowFilter,
    paginationPageChangeHandler,
    filterApplyHandler,
    filterResetHandler,
    isApplyFilter,
    conditionalRefresh,
    sortingChangeHandler,
     sortingDescriptors
  } = useStateDataGrid(pageSize, filterJson, sortingJson);
  //#endregion data grid hook

  //#region fetch data
  const getQueryParams = (): URLSearchParams => {
    if (noFilterQueryParams) {
      let mergeQueryParams = new URLSearchParams(dataGrid.computedUrlSearchParams);
      noFilterQueryParams.forEach((value, key) => {
        mergeQueryParams.append(key, value);
      });

      return mergeQueryParams;
    }

    return new URLSearchParams(dataGrid.computedUrlSearchParams);
  }

  const dataQuery: DataQuery = {
    url: url,
    routeParams: routeParams,
    params: getQueryParams()
  };
  const { data, loadStatus } = useQuery<PagedDataResultDTO<T> | T[]>(
    dataQuery,
    pageSize ? { result: [], count: 0 } : [], // if is set page size => restul will be pagaed data object
    [...externalDependencies ?? [], 
    getQueryParams().toString(), routeParams?.toString()],
    { useTracker: true },
    conditionalRefresh
  ); 
  //#endregion fetch data

  //#region sub-renders
  const renderItems = () => {
    let result: T[] = [];
    
    if (pageSize) {
      result = (data as PagedDataResultDTO<T>).result;
    } else {
      result = data as T[];
    }

    return result.map(renderItemRow);
  };

  const renderItemRow = (item: T, index: number) => {
    if (itemAsCard) {
      return <DataGridRow key={index}>{renderItem(item)}</DataGridRow>;
    } else {
      return <Fragment key={index}>{renderItem(item)}</Fragment>;
    }
  };

  const renderDataGridHeader = () => {
    let count = 0;

    if (pageSize) {
      count = (data as PagedDataResultDTO<T>).count;
    } else {
      count = (data as T[]).length;
    }

    if (renderCustomGridHeader) {
      return renderCustomGridHeader(count, isApplyFilter(), setShowFilter)
    } else {
      return (
        <DataGridHeader
          setShowFilterModal={filterJson ? setShowFilter : undefined}
          count={count}
          actionBtns={renderGridHeaderBtns}
          filterActive={isApplyFilter()}
          renderSorting={sortingJson ? renderSortingMenu : undefined}
        />
      );
    }
  };

  const renderPagination = () => {
    if (pageSize) {
      return (
        <div className="d-flex justify-content-center mt-4">
          <Pagination
            urlParams={new URLSearchParams(dataGrid.computedUrlSearchParams)}
            count={(data as PagedDataResultDTO<T>).count}
            pageSize={pageSize}
            onPageChange={paginationPageChangeHandler}
          />
        </div>
      );      
    }
  };

  const renderFilter = () => {
    if (filterJson) {
      return (
        <DataFilterModal
          descriptors={descriptors}
          setDescriptors={setDescriptors}
          applyButtonOnClick={filterApplyHandler}
          resetButtonOnClick={filterResetHandler}
          show={showFilter}
          setShow={setShowFilter}
        />
      );  
    }
  };

  const renderSortingMenu = () => {
    if (sortingJson) {
      return (
        <SortingMenu 
          id="invoices-sorting-menu"
          onSortingClick={sortingChangeHandler}
          descriptors={sortingDescriptors}
        />
      );
    }
    
    return <></>;
  }
  //#endregion sub-renders

  //#region render
  return (
    <NoContentWrapper
      data={data}
      loadStatus={loadStatus}
      noContentAsAlert
      isActiveFilter={isApplyFilter}
      noContentRender={renderNoContent}
    >
      {renderDataGridHeader()}
      {renderHeader()}
      {renderItems()}
      {renderFilter()}
      {renderPagination()}
    </NoContentWrapper>
  );
  //#endregion render
}

export default DataGridState;